import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

interface CompletedDocument {
  id: number;
  envelope_id: string;
  account_id: string;
  document_id: string;
  document_name: string;
  original_hash: string | null;
  docusign_hash: string | null;
  completed_at: string;
  signers_json: string;
  status: string;
  blockchain_tx_hash: string | null;
}

export default function Documents() {
  const [documents, setDocuments] = useState<CompletedDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDocuments();
    const interval = setInterval(loadDocuments, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadDocuments = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/documents/completed`);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
        setError(null);
      } else {
        setError('Failed to load documents');
      }
    } catch (err) {
      setError('Failed to connect to API');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const parseSigners = (signersJson: string) => {
    try {
      return JSON.parse(signersJson);
    } catch {
      return [];
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <p className="text-gray-500">Loading completed documents...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Completed Documents</h1>
          <button
            onClick={loadDocuments}
            className="text-sm text-indigo-600 hover:text-indigo-800"
          >
            Refresh
          </button>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-md mb-4">{error}</div>
        )}

        {documents.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg">No completed documents yet.</p>
            <p className="text-sm mt-2">
              Documents will appear here after signers complete the signing process.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="border rounded-lg p-4 hover:border-indigo-300 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-gray-900">{doc.document_name}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Envelope: {doc.envelope_id}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded ${
                      doc.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {doc.status}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Completed At</p>
                    <p className="font-mono">{doc.completed_at ? formatDate(doc.completed_at) : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Signers</p>
                    <div className="space-y-1">
                      {parseSigners(doc.signers_json).map((signer: { name: string; email: string }, i: number) => (
                        <p key={i} className="text-gray-700">
                          {signer.name} ({signer.email})
                        </p>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  {doc.original_hash && (
                    <div className="text-sm">
                      <span className="text-gray-500">Original Hash: </span>
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                        {doc.original_hash.substring(0, 32)}...
                      </span>
                    </div>
                  )}
                  {doc.docusign_hash && (
                    <div className="text-sm">
                      <span className="text-gray-500">DocuSign Hash: </span>
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                        {doc.docusign_hash.substring(0, 32)}...
                      </span>
                    </div>
                  )}
                  {doc.blockchain_tx_hash ? (
                    <div className="text-sm">
                      <span className="text-gray-500">Blockchain TX: </span>
                      <a
                        href={`https://etherscan.io/tx/${doc.blockchain_tx_hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-xs text-indigo-600 hover:text-indigo-800"
                      >
                        {doc.blockchain_tx_hash.substring(0, 16)}...
                      </a>
                    </div>
                  ) : (
                    <div className="text-sm text-yellow-600">
                      Pending blockchain registration
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
